/**
 * ConnectorSyncService — shared sync patterns for INT-4 legacy TMS connectors.
 *
 * Provides conflict resolution (source system wins), upsert-by-reference, and
 * sync-log helpers used by McLeod and TMW connectors.
 */

import DataService from './DataService';
import EventBusService from './EventBusService';

const ConnectorSyncService = {
  /**
   * Upsert a record into a Kairos collection by externalId.
   * Source-system wins: if a synced record already exists, it is updated.
   */
  async upsertByExternalId(collection, externalId, data, userId, source) {
    const existing = await DataService.query(collection, { externalId }, data.tenantId);
    const existing0 = existing.find(r => r.externalId === externalId && !r.isDeleted);
    if (existing0) {
      await DataService.update(collection, existing0.id, { ...data, updatedAt: new Date().toISOString() }, userId);
      return { action: 'updated', id: existing0.id };
    }
    const created = await DataService.create(collection, data, userId, source);
    return { action: 'created', id: created?.id };
  },

  /**
   * Write a sync event to EventBusService after a connector sync run.
   */
  async publishSyncEvent({ tenantId, connectorType, direction, recordsIn, recordsOut, errors, status }) {
    try {
      await EventBusService.publish({
        eventType: `int.${connectorType}.sync.${status}`,
        tenantId,
        sourceModule: 'integrations',
        sourceId: connectorType,
        actor: `${connectorType}_sync`,
        payload: { direction, recordsIn, recordsOut, errors },
        confidence: 1,
      });
    } catch (_) {
      // Non-blocking — sync log is durable in Firestore
    }
  },

  /**
   * Publish a writeback event when Kairos pushes a status change to a source TMS.
   */
  async publishWritebackEvent({ tenantId, connectorType, loadId, newStatus, causationId }) {
    try {
      await EventBusService.publish({
        eventType: `int.${connectorType}.writeback.status`,
        tenantId,
        sourceModule: 'tms',
        sourceId: loadId,
        actor: `${connectorType}_writeback`,
        causationId,
        payload: { loadId, newStatus },
        confidence: 1,
      });
    } catch (_) {}
  },
};

export default ConnectorSyncService;
